<template>
	<div class="ks-videoeditor-source-body">
		<template v-if="!selected_event">
			<k-text-field v-model="event_search" placeholder="Search events" prepend-icon="search" :loading="events_loading" clearable hide-details></k-text-field>
			<div class="ks-videoeditor-list">
				<div v-for="event in events" :key="event.uuid" class="ks-videoeditor-row" @click="select_event(event)">
					<div class="ks-videoeditor-row-main">
						<div class="ks-videoeditor-row-title">{{ event_title(event) }}</div>
						<div class="ks-videoeditor-row-sub">{{ format_event_time(event) }}</div>
					</div>
					<k-icon small>chevron_right</k-icon>
				</div>
				<div v-if="!events_loading && events.length == 0" class="ks-videoeditor-empty">No events</div>
			</div>
		</template>
		<template v-else>
			<div class="ks-videoeditor-source-head">
				<k-btn size="s" icon icon-name="arrow_back" @click="select_event(undefined)"></k-btn>
				<span class="ks-videoeditor-row-title">{{ event_title(selected_event) }}</span>
			</div>
			<k-checkbox v-model="only_own" label="Only my clips"></k-checkbox>
			<div class="ks-videoeditor-list">
				<div v-for="item in event_clip_items" :key="item.group" class="ks-videoeditor-row" @dblclick="add_event_clip(item)">
					<div class="ks-videoeditor-row-main">
						<div class="ks-videoeditor-row-title">{{ item.title }}</div>
						<div class="ks-videoeditor-row-sub">{{ fmt(item.duration) }}</div>
					</div>
					<k-btn size="s" icon icon-name="add" @click="add_event_clip(item)"></k-btn>
				</div>
				<div v-if="event_clip_items.length == 0" class="ks-videoeditor-empty">No ready clips</div>
			</div>
		</template>
	</div>
</template>

<script>
import moment from 'moment'
import EventModel from '@/models/event.js'
import {fmt} from './utils.js'

// Event picker and the ready clips of the picked event. The picked event is kept in the route.
export default {
	props: {
		event_uuid: String
	},
	data () {
		return {
			events_channel: undefined,
			events: [],
			events_loading: false,
			event_search: "",
			selected_event: undefined,
			event_info_channel: undefined,
			clips_channel: undefined,
			event_clips: {},
			only_own: false
		}
	},
	created () {
		this.generation = 0;
	},
	mounted () {
		this.connect_events();
		this.open_event_uuid(this.event_uuid);
	},
	beforeDestroy () {
		clearTimeout(this.search_timer);
		if (this.clips_channel) this.clips_channel.leave();
		if (this.event_info_channel) this.event_info_channel.leave();
	},
	watch: {
		event_uuid (uuid) {
			this.open_event_uuid(uuid);
		},
		"event_info_channel.data" (val) {
			if (!val || !this.event_uuid || val.uuid != this.event_uuid) return;
			this.event_info_channel.leave();
			this.event_info_channel = undefined;
			this.$emit("opened");
			this.select_event(val, true);
		},
		event_search () {
			clearTimeout(this.search_timer);
			this.search_timer = setTimeout(this.filter_events, 400);
		},
		"clips_channel.data" (val) {
			if (!val) return;
			_.each(val.clips, (clip) => { this.$set(this.event_clips, clip.uuid, clip); });
			if (val.clip) this.$set(this.event_clips, val.clip.uuid, val.clip);
		}
	},
	computed: {
		account () {
			return this.$store.state.account;
		},
		event_clip_items () {
			const groups = _.groupBy(Object.values(this.event_clips), (clip) => {
				return _.get(clip, "meta.group_uuid") || clip.uuid;
			});
			const items = [];
			_.each(groups, (qualities, group) => {
				const ordered = _.orderBy(qualities, (c) => _.get(c, "meta.quality.order_num", 0), ["asc"]);
				const clip = _.find(ordered, {status: "ready"});
				if (!clip || !clip.url) return;
				if (this.only_own && _.get(clip, "meta.account") != _.get(this.account, "email")) return;
				items.push({
					group: group,
					clip: clip,
					title: _.get(clip, "meta.title") || "Clip",
					duration: Number(_.get(clip, "meta.metadata.duration")) || (clip.end_time - clip.start_time),
					created: clip.inserted_at,
					qualities: _.map(_.filter(ordered, (q) => q.status == "ready" && q.url), (q) => ({
						uuid: q.uuid,
						url: q.url,
						name: _.get(q, "meta.quality.name"),
						title: _.get(q, "meta.quality.title"),
						subtitle: _.get(q, "meta.quality.subtitle")
					})),
					poster_url: _.get(clip, "meta.poster_url") || _.get(_.find(ordered, (q) => _.get(q, "meta.poster_url")), "meta.poster_url")
				});
			});
			return _.orderBy(items, ["created"], ["desc"]);
		}
	},
	methods: {
		fmt,
		connect_events () {
			this.events_channel = this.$cloudserver.channel("events:all");
			this.events_channel.on("init", () => {
				if (!this._isDestroyed) this.filter_events();
			});
			// Shared channel may already be joined: "init" won't fire again
			if (!this.events_channel.join()) this.filter_events();
		},
		filter_events () {
			if (!this.events_channel) return;
			const filters = {
				daterange: {
					start: 0,
					end: moment().endOf("day").unix()
				},
				states: ["active"],
				offset: 0,
				limit: 30,
				order_by: [{"cached_start_time": "desc"}, {"uuid": "desc"}]
			};
			if (this.event_search && this.event_search.length > 2) filters.search = this.event_search;
			const generation = ++this.generation;
			this.events_loading = true;
			this.events_channel.push("filter_reply", filters)
				.receive("ok", (msg) => {
					if (generation !== this.generation) return;
					this.events_loading = false;
					this.events = _.sortBy(msg.events, ["cached_start_time"]).reverse();
				})
				.receive("error", () => { if (generation === this.generation) this.events_loading = false; })
				.receive("timeout", () => { if (generation === this.generation) this.events_loading = false; });
		},
		select_event (event, from_url) {
			if (this.clips_channel) this.clips_channel.leave();
			this.clips_channel = undefined;
			this.event_clips = {};
			this.selected_event = event;
			if (event) {
				this.clips_channel = this.$cloudserver.channel("events:clips:" + event.uuid);
				this.clips_channel.join();
			}
			if (!from_url && (event ? event.uuid : undefined) != this.event_uuid) {
				this.$router.replace("/cloudrec/video-editor" + (event ? "/" + event.uuid : "")).catch(() => {});
			}
		},
		open_event_uuid (uuid) {
			if (this.event_info_channel) this.event_info_channel.leave();
			this.event_info_channel = undefined;
			if (!uuid) {
				if (this.selected_event) this.select_event(undefined, true);
				return;
			}
			if (this.selected_event && this.selected_event.uuid == uuid) return;
			this.event_info_channel = this.$cloudserver.channel("events:" + uuid);
			this.event_info_channel.join();
		},
		event_title (event) {
			return EventModel.title(event);
		},
		format_event_time (event) {
			return moment.unix(event.cached_start_time).format("L LT");
		},
		add_event_clip (item) {
			this.$emit("add-clip", {
				title: item.title,
				url: item.clip.url,
				poster_url: item.poster_url,
				qualities: item.qualities,
				duration: item.duration,
				source: {type: "event", event_uuid: _.get(this.selected_event, "uuid"), clip_uuid: item.clip.uuid, group_uuid: item.group, quality: _.get(item.clip, "meta.quality.name")}
			});
		}
	}
}
</script>

<style scoped>
.ks-videoeditor-source-body { padding-top: 8px; }
.ks-videoeditor-source-head { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.ks-videoeditor-list { margin-top: 8px; }
</style>
